import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import client from '../../api/client';
import Layout from '../../components/Layout';
import './Settings.css';
import './Team.css';

const ROLE_CONFIG = {
  admin:    { label: 'Admin',    color: '#3b82f6', bg: 'rgba(59,130,246,0.15)' },
  manager:  { label: 'Manager',  color: '#8b5cf6', bg: 'rgba(139,92,246,0.15)' },
  operator: { label: 'Operator', color: '#6b7280', bg: 'rgba(107,114,128,0.15)' },
};

export default function Team() {
  const { user: currentUser }     = useAuth();
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser]   = useState(null);
  const [form, setForm]           = useState({ email: '', password: '', role: 'operator', name: '' });
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [saving, setSaving]       = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await client.get('/org/users');
      setUsers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditUser(null);
    setForm({ email: '', password: '', role: 'operator', name: '' });
    setError('');
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ email: u.email, password: '', role: u.role, name: u.name || '' });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (editUser) {
        const payload = { role: form.role, name: form.name };
        if (form.password) payload.password = form.password;
        await client.put(`/org/users/${editUser.id}`, payload);
      } else {
        await client.post('/org/users', form);
      }
      setShowModal(false);
      setSuccess(editUser ? 'User updated' : 'User added');
      setTimeout(() => setSuccess(''), 3000);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    if (u.id === currentUser?.id) return;
    try {
      await client.put(`/org/users/${u.id}`, { is_active: !u.is_active });
      loadData();
    } catch (err) { console.error(err); }
  };

  const getInitials = (u) => {
    if (u.name) return u.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    return u.email[0].toUpperCase();
  };

  if (loading) return <Layout><div style={{color:'#6b7280',padding:'20px'}}>Loading...</div></Layout>;

  return (
    <Layout>
      <div className="spage-header">
        <h1>Team</h1>
        <p className="spage-sub">Manage users and their access levels.</p>
      </div>

      {success && <div className="spage-success">{success}</div>}

      <div className="spage-section">
        <div className="team-section-header">
          <div>
            <div className="spage-section-title" style={{marginBottom:'2px'}}>Members</div>
            <div className="spage-hint">{users.length} user{users.length !== 1 ? 's' : ''}</div>
          </div>
          <button className="spage-btn-save" style={{marginTop:0}} onClick={openAdd}>
            + Add User
          </button>
        </div>

        <table className="team-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Department</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan="6" className="team-empty">No users yet.</td></tr>
            )}
            {users.map(u => {
              const rc = ROLE_CONFIG[u.role] || ROLE_CONFIG.operator;
              const isMe = u.id === currentUser?.id;
              return (
                <tr key={u.id} className={!u.is_active ? 'team-row-inactive' : ''}>
                  <td className="team-name-cell">
                    <div className="team-avatar" style={{background: rc.bg, color: rc.color}}>
                      {getInitials(u)}
                    </div>
                    <span>{u.name || '--'}</span>
                  </td>
                  <td className="team-email-cell">{u.email}</td>
                  <td>
                    <span className="team-role-badge" style={{color: rc.color, background: rc.bg}}>
                      {rc.label}
                    </span>
                  </td>
                  <td className="team-dept">--</td>
                  <td>
                    <span className={`team-status-dot ${u.is_active ? 'active' : 'inactive'}`}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="team-actions">
                      <button className="team-action-btn" onClick={() => openEdit(u)}>Edit</button>
                      <button
                        className={`team-action-btn ${u.is_active ? 'danger' : 'success'}`}
                        onClick={() => toggleActive(u)}
                        disabled={isMe}
                      >
                        {u.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="kiosk-overlay" onClick={() => setShowModal(false)}>
          <div className="kiosk-modal" onClick={e => e.stopPropagation()}>
            <div className="kiosk-modal-header">
              <h2>{editUser ? 'Edit User' : 'Add User'}</h2>
              <button className="kiosk-modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            {error && <div className="spage-error">{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="spage-field">
                <label>Name</label>
                <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="John Smith" />
              </div>
              <div className="spage-field">
                <label>Email {!editUser && '*'}</label>
                <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="john@shop.com" required={!editUser} disabled={!!editUser} />
              </div>
              <div className="spage-field">
                <label>{editUser ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                <input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="••••••••" required={!editUser} />
              </div>
              <div className="spage-field">
                <label>Role</label>
                <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
                  <option value="operator">Operator</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="kiosk-modal-actions">
                <button type="button" className="spage-btn-upgrade" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="spage-btn-save" style={{marginTop:0}} disabled={saving}>
                  {saving ? 'Saving...' : editUser ? 'Save Changes' : 'Add User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}